import { Component, Input, computed, effect, signal, Output, EventEmitter } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { FormWidget, FormField, Theme } from '../../models/reader-document';
import { ElementRef, ViewChild } from '@angular/core';
import { ensureGoogleMapsLoaded } from '../../utils/google-maps-loader';
import { DecimalPipe } from '@angular/common';

type DarkTheme = Theme & { isDark?: boolean };
type MapValue = { lat: number; lng: number } | null;

@Component({
  selector: 'wr-form-widget',
  standalone: true,
  imports: [ReactiveFormsModule, DecimalPipe],
  templateUrl: './form.component.html',
})
export class FormWidgetComponent {

  private widgetSig = signal<FormWidget | null>(null);
  private themeSig = signal<DarkTheme | null>(null);
  private successTimeout: any = null;

  form = signal<FormGroup | null>(null);
  submitted = signal(false); // validation errors
  success = signal(false);
  submittedOnce = signal(false);    

  @Output() prepared = new EventEmitter<any>();

  constructor(private fb: FormBuilder) {
    effect(() => {
      const w = this.widgetSig();
      if (!w) return;

      // Reset form
      this.submitted.set(false);
      this.success.set(false);
      this.submittedOnce.set(false);  

      const group: Record<string, any> = {};

      for (const section of w.sections ?? []) {
        for (const field of section.fields ?? []) {
          group[this.ctrlName(section.id, field)] = this.createControl(field);
        }
      }

      this.form.set(this.fb.group(group));
    });
  }

  @Input() set json(value: FormWidget) {
    this.widgetSig.set(value);
  }

  @Input() set theme(value: Theme | undefined) {
    this.themeSig.set((value as DarkTheme) ?? null);
  }

  widget = computed(() => this.widgetSig());
  fg = computed(() => this.form());
  isDark = computed(() => !!this.themeSig()?.isDark);

  private colors = computed(() => {
    const t = this.themeSig();
    if (!t) return null;
    return t.isDark ? t.darkColors : t.lightColors;
  });

  titleColor = computed(() => this.colors()?.title ?? this.colors()?.primary ?? '#111827');
  subtitleColor = computed(() => this.colors()?.subtitle ?? this.colors()?.secondary ?? '#4b5563');
  textColor = computed(() => this.colors()?.primary ?? '#111827');
  backgroundColor = computed(() => this.colors()?.background ?? '#f9fafb');
  accent = computed(() => this.colors()?.accent ?? '#ff5e38');
  cornerRadius = computed(() => this.themeSig()?.cornerRadius ?? 12);
  titleFont = computed(() => this.themeSig()?.fonts?.title?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');
  subtitleFont = computed(() => this.themeSig()?.fonts?.subtitle?.name ?? this.themeSig()?.fonts?.plain?.name ?? 'inherit');
  plainFont = computed(() => this.themeSig()?.fonts?.plain?.name ?? 'inherit');


  ctrlName(sectionId: string, field: FormField): string {
    return `${sectionId}__${field.id}`;
  }

  private createControl(field: FormField) {
    const validators = [];

    if (field.mandatory) {
      if (field.type === 'boolean') {
        validators.push(Validators.requiredTrue);
      } else {
        validators.push(Validators.required);
      }
    }

    switch (field.type) {
      case 'boolean':
        return this.fb.control(field.mandatory ? false : true, validators);
      default:
        return this.fb.control('', validators);
    }
  }

  fieldOptions(field: any): any[] {
    const opts = (field as any).options as any[] | undefined;
    return opts ?? [];
  }

  optionValue(opt: any): string {
    return typeof opt === 'string' ? opt : opt.value;
  }

  optionTitle(opt: any): string {
    return typeof opt === 'string' ? opt : opt.title;
  }

  trackOption(opt: any): string {
    if (typeof opt === 'string') return opt;
    return opt.id ?? opt.value;
  }

  // UI helpers

  control(sectionId: string, field: FormField): AbstractControl | null {
    return this.fg()?.get(this.ctrlName(sectionId, field)) ?? null;
  }

  showError(sectionId: string, field: FormField): boolean {
    const c = this.control(sectionId, field);
    if (!c) return false;
    return (c.touched || this.submitted()) && !!c.errors;
  }

  onSubmit() {
    const w = this.widget();
    const fg = this.fg();
    if (!w || !fg) return;

    this.submitted.set(true);
    fg.markAllAsTouched();

    if (fg.invalid) return;

    const data: Record<string, any> = {};

    for (const section of w.sections ?? []) {
      for (const field of section.fields ?? []) {
        const name = this.ctrlName(section.id, field);
        const raw = fg.get(name)?.value;

        data[field.key] = this.coerceValue(field, raw);
      }
    }

    const payload = {
      widgetId: w.id,
      timeStamp: new Date().toISOString(),
      data,
      workflowTemplateId: (w as any).workflowTemplateId ?? undefined,
      reloadDPP: (w as any).reloadDPP ?? false,
    };

    this.prepared.emit(payload);
    console.log('Prepared form payload:', payload);

    if (!(w as any).reloadDPP) {
      this.submittedOnce.set(true);
      this.success.set(true);

      fg.reset();
      this.submitted.set(false); 

      if (this.successTimeout) {
        clearTimeout(this.successTimeout);
      }
      this.successTimeout = setTimeout(() => {
        this.success.set(false);
      }, 5000);
    }

    /* if (!(w as any).reloadDPP) {
      this.submittedOnce.set(true);
      this.success.set(true);
      // Auto-hide
      if (this.successTimeout) {
        clearTimeout(this.successTimeout);
      }
      this.successTimeout = setTimeout(() => {
        this.success.set(false);
      }, 5000);
    } */

  }

  private coerceValue(field: FormField, raw: any) {
    if (raw === '' || raw === null || raw === undefined) return raw;

    switch (field.type) {
      case 'number':
        return raw === '' ? null : parseInt(raw, 10);
      case 'decimal':
        return raw === '' ? null : parseFloat(raw);
      case 'boolean':
        // store boolean
        return !!raw;
      case 'map':
        // return {lat,lng}
        return raw;
      default:
        return raw;
    }
  }

  // button style
  submitBtnStyle = computed(() => {
    const w = this.widget();
    const t = this.themeSig();
    const colors = this.colors();
    const s = (w as any)?.submitButtonStyle ?? {};
    return {
      background: s.backgroundColor ?? colors?.accent ?? '#ff5e38',
      border: s.borderColor ?? 'transparent',
      color: s.foregroundColor ?? '#ffffff',
      borderWidth: s.borderWidth ?? 0,
      radius: s.cornerRadius ?? t?.cornerRadius ?? 12,
    };
  });

  hasRequiredFields = computed(() => {
    const widget = this.widget();
    if (!widget?.sections) return false;

    return widget.sections.some(section =>
        section.fields?.some(f => f.mandatory)
    );
  });

  requiredHint = computed(() => {
    //const lang = this.activeLang?.() ?? 'en';
    const lang = navigator.language;

    switch (lang) {
        case 'de-DE':
        return '* = Pflichtfeld';
        case 'es-EN':
        return '* = Campo obligatorio';
        default:
        return '* = Required field';
    }
  });

  mapText(key: 'noLocation' | 'choose' | 'select' | 'chooseLocation' | 'cancel'): string {
    const lang = (navigator.language || 'en').toLowerCase();
    const de = lang.startsWith('de');

    switch (key) {
      case 'noLocation':
        return de ? 'Kein Standort ausgewählt' : 'No location selected';
      case 'select':
        return de ? 'Auswählen' : 'Select';
      case 'chooseLocation':
        return de ? 'Standort wählen' : 'Choose location';
      case 'cancel':
        return de ? 'Abbrechen' : 'Cancel';
      default:
        return '';
    }
  }

  // GOOGLE MAPS

  @ViewChild('mapModalContainer') mapModalContainer?: ElementRef<HTMLDivElement>;
  mapModalOpen = signal(false);
  private mapModalField = signal<{ sectionId: string; fieldId: string } | null>(null);
  private mapModalValue = signal<MapValue>(null);
  private mapInstance: any = null;
  private mapMarker: any = null;

  mapValue(sectionId: string, field: any): MapValue {
    const name = this.ctrlName(sectionId, field);
    const form = this.form();
    const raw = form?.get(name)?.value;

    if (!raw) return null;

    if (typeof raw === 'string') {
      const [lat, lng] = raw.split(',').map((x) => parseFloat(x.trim()));
      return isFinite(lat) && isFinite(lng) ? { lat, lng } : null;
    }

    if (typeof raw?.lat === 'number' && typeof raw?.lng === 'number') {
      return raw as MapValue;
    }

    return null;
  }

  private async renderPreviewMap(
    sectionId: string,
    fieldId: string,
    value: MapValue | null
  ) {
    if (!value) return;

    try {
      await ensureGoogleMapsLoaded();
    } catch (err) {
      console.error('Google Maps failed to load for preview', err);
      return;
    }

    const elId = `map-preview-${sectionId}-${fieldId}`;
    const el = document.getElementById(elId) as HTMLDivElement | null;
    if (!el) return; // no preview container (showsMap false or not in DOM)

    const googleAny = (window as any).google;

    const map = new googleAny.maps.Map(el, {
      center: value,
      zoom: 14,
      disableDefaultUI: true,
    });

    new googleAny.maps.Marker({
      position: value,
      map,
    });
  }

  showsMap(field: any) {
    return !!field?.showsMap;
  }

  async openMap(sectionId: string, field: any) {
    this.mapModalField.set({ sectionId, fieldId: field.id });

    this.mapModalValue.set(this.mapValue(sectionId, field));
    this.mapModalOpen.set(true);

    requestAnimationFrame(() => this.initMapWithRetry());
  }

private initMapWithRetry(tries = 0) {
  const host = this.mapModalContainer?.nativeElement;

  if (!host) {
    if (tries < 10) requestAnimationFrame(() => this.initMapWithRetry(tries + 1));
    return;
  }

  this.initMap();
}

  closeMap() {
    this.mapModalOpen.set(false);
    this.mapInstance = null;
    this.mapMarker = null;
  }

  private async initMap() {
    const host = this.mapModalContainer?.nativeElement;
    if (!host) return;

    try {
      await ensureGoogleMapsLoaded();
    } catch (err) {
      console.error('Google Maps failed to load', err);
      return;
    }

    const current = this.mapModalValue();
    const center = current ?? { lat: 47.0, lng: 8.0 };

    const googleAny = (window as any).google;
    this.mapInstance = new googleAny.maps.Map(host, {
      center,
      zoom: current ? 14 : 4,
      disableDefaultUI: true,
    });

    const g = (window as any).google;

    if (g?.maps?.marker?.AdvancedMarkerElement) {
      this.mapMarker = new g.maps.marker.AdvancedMarkerElement({
        map: this.mapInstance,
        position: center,
        gmpDraggable: true,
      });
    } else {
      // fallback for old browsers
      this.mapMarker = new g.maps.Marker({
        map: this.mapInstance,
        position: center,
        draggable: true,
      });
    }

    this.mapInstance.addListener('click', (e: any) => {
      const pos = e.latLng;
      if (!pos) return;
      const value = { lat: pos.lat(), lng: pos.lng() };
      this.mapModalValue.set(value);
      this.mapMarker.setPosition(pos);
    });
  }

  confirmMapSelection() {
    const target = this.mapModalField();
    const value = this.mapModalValue();

    if (!target) {
      this.closeMap();
      return;
    }

    const name = this.ctrlName(target.sectionId, { id: target.fieldId } as any);
    const form = this.form();
    const ctrl = form?.get(name);

    if (ctrl) {
      ctrl.setValue(value);
      ctrl.markAsDirty();
      ctrl.markAsTouched();
      ctrl.updateValueAndValidity();
      this.renderPreviewMap(target.sectionId, target.fieldId, value);
    }

    this.closeMap();
  }

  validateNumberKeys(event: KeyboardEvent) {
    const blockedKeys = ['.', ',', '-', 'e', 'E', '+'];
    if (blockedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }
  validateDecimalKeys(event: KeyboardEvent) {
    const blockedKeys = ['-', 'e', 'E', '+'];
    if (blockedKeys.includes(event.key)) {
      event.preventDefault();
    }
  }

}